import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { auth } from './Firebase';
import { useAuth } from '../contexts/AuthContext';

export default function BillingResultPage({ status }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, refreshRoles } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(status === 'success');

  useEffect(() => {
    if (status !== 'success' || loading || !user) {
      setChecking(false);
      return;
    }

    const refreshBillingStatus = async () => {
      setChecking(true);
      setError(null);
      try {
        const token = await auth.currentUser?.getIdToken(true);
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');

        if (sessionId) {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/billing/confirm-session`,
            { sessionId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubscription(response.data?.subscription || null);
        await refreshRoles();
        window.setTimeout(() => navigate('/konto/subskrypcja', { replace: true }), 1200);
      } catch (err) {
        setError(err.response?.data?.message || err.response?.data?.error || err.message);
      } finally {
        setChecking(false);
      }
    };

    refreshBillingStatus();
  }, [status, loading, user, refreshRoles, location.search, navigate]);

  const isSuccess = status === 'success';
  const Icon = isSuccess ? CheckCircleIcon : ErrorOutlineIcon;

  return (
    <Paper sx={{ maxWidth: 640, mx: 'auto', my: 6, p: 4, textAlign: 'center' }}>
      <Icon sx={{ fontSize: 48, color: isSuccess ? '#64ffda' : '#ffca28', mb: 2 }} />
      <Typography variant="h5" fontWeight={900} gutterBottom>
        {isSuccess ? 'Dziękujemy za zakup' : 'Płatność została przerwana'}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {isSuccess
          ? 'Jeżeli płatność została potwierdzona przez Stripe, dostęp pojawi się na Twoim koncie automatycznie.'
          : 'Możesz wrócić do wyboru pakietu i dokończyć zakup w dowolnym momencie.'}
      </Typography>

      {checking && (
        <Box sx={{ mb: 3 }}>
          <CircularProgress size={26} />
        </Box>
      )}

      {subscription && (
        <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
          Status subskrypcji: {subscription.status}. Za chwilę przeniesiemy Cię do szczegółów konta.
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
          Nie udało się jeszcze potwierdzić statusu: {error}
        </Alert>
      )}

      {!isSuccess && (
        <Button component={RouterLink} to="/vip" variant="contained">
          Wróć do pakietów
        </Button>
      )}
    </Paper>
  );
}
